// PRD.322 — Rich Text editing and rendering for the Form Builder.
//
// NO NEW DEPENDENCIES. The monorepo has no rich-text editor and no HTML
// sanitizer, so rather than adding both this uses `contentEditable` for input
// and converts the browser's DOM into the restricted `RichTextDoc` model on
// every change.
//
// The safety property comes from the conversion direction:
//
//   editing:   DOM  ──whitelist walk──►  RichTextDoc
//   rendering: RichTextDoc  ──React elements──►  screen
//
// HTML is never stored and never rendered. `dangerouslySetInnerHTML` does not
// appear here, so injected markup cannot become markup — it can only ever end
// up as the `text` of a span. The DOM walk is a structural whitelist, not a
// regex filter, which is what the PRD requires.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  parseRichText,
  stringifyRichText,
  type RichTextBlock,
  type RichTextDoc,
  type RichTextMark,
} from '@helios/data-access';

// ─── Rendering ───

/**
 * Render one span's marks as nested React elements.
 *
 * Only the three V1 marks map to a tag; anything else was already dropped at
 * parse time, so this cannot emit an unexpected element.
 */
function renderSpan(text: string, marks: RichTextMark[] | undefined, key: number) {
  let node: React.ReactNode = text;
  if (marks?.includes('underline')) node = <u key="u">{node}</u>;
  if (marks?.includes('italic')) node = <em key="e">{node}</em>;
  if (marks?.includes('bold')) node = <strong key="s">{node}</strong>;
  return <span key={key}>{node}</span>;
}

function renderBlockContent(block: RichTextBlock) {
  if (block.spans.length === 0) return <br />;
  return block.spans.map((s, i) => renderSpan(s.text, s.marks, i));
}

/**
 * Read-only formatted view. The SAME component is used by the builder canvas,
 * Preview and the runtime form, so the three can never disagree about how a
 * consent paragraph looks.
 */
export function RichTextView({ doc, style }: { doc: RichTextDoc; style?: React.CSSProperties }) {
  // Consecutive list blocks are grouped so a bulleted list renders as one list
  // rather than a stack of single-item lists.
  const groups = useMemo(() => {
    const out: { type: 'paragraph' | 'bullet' | 'number'; blocks: RichTextBlock[] }[] = [];
    for (const block of doc.blocks) {
      const last = out[out.length - 1];
      if (last && last.type === block.type && block.type !== 'paragraph') {
        last.blocks.push(block);
      } else {
        out.push({ type: block.type, blocks: [block] });
      }
    }
    return out;
  }, [doc]);

  return (
    <div style={{ fontSize: 'var(--font-size-sm)', lineHeight: 1.6, ...style }}>
      {groups.map((g, gi) => {
        if (g.type === 'paragraph') {
          return g.blocks.map((b, bi) => (
            <p key={`${gi}-${bi}`} style={{ margin: '0 0 8px' }}>
              {renderBlockContent(b)}
            </p>
          ));
        }
        const ListTag = g.type === 'bullet' ? 'ul' : 'ol';
        return (
          <ListTag key={gi} style={{ margin: '0 0 8px', paddingLeft: 22 }}>
            {g.blocks.map((b, bi) => (
              <li key={bi}>{renderBlockContent(b)}</li>
            ))}
          </ListTag>
        );
      })}
    </div>
  );
}

// ─── DOM → model ───

const TAG_MARKS: Record<string, RichTextMark> = {
  B: 'bold',
  STRONG: 'bold',
  I: 'italic',
  EM: 'italic',
  U: 'underline',
};

/**
 * Convert a contentEditable subtree into the restricted model.
 *
 * Everything not explicitly recognised is ignored — an `<img>`, `<script>`,
 * `<table>` or styled `<span>` pasted from Word contributes its TEXT and
 * nothing else. That is the whitelist: unknown structure cannot survive,
 * because only known structure is ever read.
 */
function domToRichText(root: HTMLElement): RichTextDoc {
  const blocks: RichTextBlock[] = [];

  function collectSpans(node: Node, marks: RichTextMark[], into: RichTextBlock) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent ?? '';
      if (text) into.spans.push(marks.length ? { text, marks: [...marks] } : { text });
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;
    if (el.tagName === 'BR') return;
    const mark = TAG_MARKS[el.tagName];
    const nextMarks = mark && !marks.includes(mark) ? [...marks, mark] : marks;
    for (const child of Array.from(el.childNodes)) collectSpans(child, nextMarks, into);
  }

  function pushBlock(type: RichTextBlock['type'], node: Node) {
    const block: RichTextBlock = { type, spans: [] };
    collectSpans(node, [], block);
    blocks.push(block);
  }

  function walk(node: Node) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent ?? '';
      // Bare text directly under the root (the browser's first line before any
      // block element exists) still deserves a paragraph.
      if (text.trim()) blocks.push({ type: 'paragraph', spans: [{ text }] });
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;

    if (el.tagName === 'UL' || el.tagName === 'OL') {
      const type = el.tagName === 'UL' ? 'bullet' : 'number';
      for (const li of Array.from(el.children)) {
        if (li.tagName === 'LI') pushBlock(type, li);
      }
      return;
    }
    if (el.tagName === 'DIV' || el.tagName === 'P') {
      pushBlock('paragraph', el);
      return;
    }
    if (el.tagName === 'BR') {
      blocks.push({ type: 'paragraph', spans: [] });
      return;
    }
    // Any other element: treat its contents as inline text in a paragraph.
    pushBlock('paragraph', el);
  }

  for (const child of Array.from(root.childNodes)) walk(child);
  return { version: 1, blocks };
}

/**
 * Build the initial DOM for the editor.
 *
 * Elements are created with `createElement`/`textContent` rather than an HTML
 * string, so even here no markup is ever parsed.
 */
function docToDom(doc: RichTextDoc, root: HTMLElement) {
  root.textContent = '';
  let listEl: HTMLElement | null = null;
  let listType: string | null = null;

  for (const block of doc.blocks) {
    const inline = document.createDocumentFragment();
    if (block.spans.length === 0) {
      inline.appendChild(document.createElement('br'));
    }
    for (const span of block.spans) {
      let node: Node = document.createTextNode(span.text);
      if (span.marks?.includes('underline')) {
        const u = document.createElement('u');
        u.appendChild(node);
        node = u;
      }
      if (span.marks?.includes('italic')) {
        const em = document.createElement('em');
        em.appendChild(node);
        node = em;
      }
      if (span.marks?.includes('bold')) {
        const strong = document.createElement('strong');
        strong.appendChild(node);
        node = strong;
      }
      inline.appendChild(node);
    }

    if (block.type === 'paragraph') {
      listEl = null;
      listType = null;
      const div = document.createElement('div');
      div.appendChild(inline);
      root.appendChild(div);
      continue;
    }

    const tag = block.type === 'bullet' ? 'UL' : 'OL';
    if (!listEl || listType !== tag) {
      listEl = document.createElement(tag.toLowerCase());
      listType = tag;
      root.appendChild(listEl);
    }
    const li = document.createElement('li');
    li.appendChild(inline);
    listEl.appendChild(li);
  }
}

// ─── Editor ───

interface ToolbarButton {
  label: string;
  title: string;
  command: string;
  style?: React.CSSProperties;
}

/**
 * V1 toolbar exactly as specified: Bold, Italic, Underline, bulleted and
 * numbered lists, Undo, Redo. No source mode, images, tables, fonts, sizes or
 * colours — anything the model cannot represent is deliberately absent rather
 * than offered and silently dropped.
 */
const TOOLBAR: ToolbarButton[] = [
  { label: 'B', title: 'Bold', command: 'bold', style: { fontWeight: 800 } },
  { label: 'I', title: 'Italic', command: 'italic', style: { fontStyle: 'italic' } },
  { label: 'U', title: 'Underline', command: 'underline', style: { textDecoration: 'underline' } },
  { label: '••', title: 'Bulleted list', command: 'insertUnorderedList' },
  { label: '1.', title: 'Numbered list', command: 'insertOrderedList' },
  { label: '↶', title: 'Undo', command: 'undo' },
  { label: '↷', title: 'Redo', command: 'redo' },
];

export function RichTextEditor({
  value,
  onChange,
  minHeight = 160,
  disabled = false,
  ariaLabel = 'Rich text',
}: {
  /** Serialized `RichTextDoc`. */
  value: string;
  onChange: (next: string) => void;
  minHeight?: number;
  disabled?: boolean;
  ariaLabel?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  // Tracks what we last emitted, so re-renders driven by our own onChange do
  // not rebuild the DOM and destroy the caret position mid-typing.
  const emittedRef = useRef<string | null>(null);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (emittedRef.current === value) return;
    docToDom(parseRichText(value), el);
    emittedRef.current = value;
  }, [value]);

  const emit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const next = stringifyRichText(domToRichText(el));
    emittedRef.current = next;
    onChange(next);
  }, [onChange]);

  const exec = useCallback(
    (command: string) => {
      if (disabled) return;
      ref.current?.focus();
      // `execCommand` is deprecated but is the only zero-dependency way to get
      // native undo/redo and selection-aware formatting. Its OUTPUT is never
      // trusted: whatever it produces is re-read through `domToRichText`, so
      // the stored model stays inside the whitelist regardless.
      document.execCommand(command);
      emit();
    },
    [disabled, emit],
  );

  return (
    <div
      style={{
        border: `1px solid ${focused ? 'var(--color-primary, #2563eb)' : 'var(--border)'}`,
        borderRadius: 'var(--radius-sm)',
        overflow: 'hidden',
        background: disabled ? 'var(--bg-surface-2, #f8fafc)' : 'var(--bg-surface)',
      }}
    >
      {!disabled && (
        <div
          style={{
            display: 'flex',
            gap: 2,
            padding: 4,
            borderBottom: '1px solid var(--border)',
            background: 'var(--bg-surface-2, #f8fafc)',
            flexWrap: 'wrap',
          }}
        >
          {TOOLBAR.map((b) => (
            <button
              key={b.command}
              type="button"
              title={b.title}
              aria-label={b.title}
              // Keeps the selection in the editor — without this the button
              // steals focus and the command applies to nothing.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => exec(b.command)}
              style={{
                minWidth: 28,
                height: 26,
                border: '1px solid transparent',
                borderRadius: 'var(--radius-sm)',
                background: 'transparent',
                color: 'var(--text)',
                fontSize: 'var(--font-size-xs)',
                cursor: 'pointer',
                ...b.style,
              }}
            >
              {b.label}
            </button>
          ))}
        </div>
      )}
      <div
        ref={ref}
        role="textbox"
        aria-multiline="true"
        aria-label={ariaLabel}
        contentEditable={!disabled}
        suppressContentEditableWarning
        onInput={emit}
        onBlur={() => {
          setFocused(false);
          emit();
        }}
        onFocus={() => setFocused(true)}
        style={{
          minHeight,
          maxHeight: 420,
          overflowY: 'auto',
          padding: '8px 10px',
          fontSize: 'var(--font-size-sm)',
          lineHeight: 1.6,
          color: 'var(--text)',
          outline: 'none',
        }}
      />
    </div>
  );
}
